import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Injector, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@services/notification.service';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { TransferErrorPopupComponent } from '@shared/components/transfer-error-popup/transfer-error-popup.component';
import { aciesDateFormat } from '@shared/helpers';
import { GenericTableColumn } from '@shared/models/generictablecolumn';
import { UserToNamePipe } from '@shared/pipes/user-to-name.pipe';
import { CuttingService, IResponseRule, IRule } from '@shared/services/cutting.service';
import { CuttingRuleExcelService } from '@shared/services/export-import-services/cutting-rule-excel.service';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { catchError, finalize, map, Subject, switchMap, takeUntil, tap, throwError } from 'rxjs';
import { IAccount } from '../../../shared/services/user.service';
import { CreateUpdateRuleDialogComponent } from './create-update-rule/create-update-rule';

@Component({
	selector: 'app-rules',
	templateUrl: './rules.component.html',
	styleUrls: ['./rules.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class RulesComponent implements OnInit {
	public isLoading: boolean = false;
	public showSearch: boolean = false;
	public tableSource = new MatTableDataSource<IRule>();
	public rulesFilter: string = '';
	public pageSizes = [10, 20, 50, 100];
	public initialPageSize = 20;
	public selected: object[] = [];
	public unsubscribe: Subject<void> = new Subject<void>();
	public loadingSubject: Subject<boolean> = new Subject<boolean>();
	public countSubject$: Subject<number> = new Subject<number>();
	public count$ = this.countSubject$.asObservable();
	public setupNames: Map<string, string> = new Map<string, string>();

	activeKey = 'general.table.status.active';
	inactiveKey = 'general.table.status.inactive';

	public registerTableColumns: GenericTableColumn[] = [
		{ name: 'masterdata.cutting.name', dataKey: 'name' },
		{ name: 'masterdata.cutting.priority', dataKey: 'priority' },
		{
			name: 'masterdata.cutting.disableOptimization',
			dataKey: 'disableOptimizationLabel',
			isTranslatable: true,
		},
		{ name: 'masterdata.cutting.description', dataKey: 'description' },
		{ name: 'masterdata.cutting.batch-type', dataKey: 'batchType' },
		{ name: 'masterdata.cutting.material', dataKey: 'material' },
		{ name: 'masterdata.cutting.machines.machine', dataKey: 'machine' },
		{ name: 'masterdata.cutting.setup', dataKey: 'setupName' },
		{ name: 'masterdata.cutting.updatedDate', dataKey: 'updatedDate', sortKey: 'updatedAtEpoch' },
		{
			name: 'masterdata.cutting.updatedBy',
			dataKey: 'updatedBy',
			pipe: UserToNamePipe,
			sortAsShown: true,
		},
	];

	public regRowActions: object[] = [
		{
			title: 'masterdata.cutting.actions.edit',
			handler: 'editMasterdataRow',
		},
		{
			title: 'masterdata.cutting.actions.delete',
			handler: 'deleteMasterdataRow',
		},
	];

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'masterdata.cutting.newbtn.rules',
			click: () => this.openCreateUpdateRule(),
		},
	];

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_export',
			text: () => 'masterdata.cutting.ruleImportExport.export.button',
			click: () => this.exportRules(),
			permissions: () => ['cutting:RuleList'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.cutting.ruleImportExport.import.button',
			click: () => this.onImport(),
			permissions: () => ['cutting:RulePost', 'cutting:RulePut', 'cutting:RuleList'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.cutting.ruleImportExport.import.template.button',
			click: () => this.downloadTemplate(),
			permissions: () => ['cutting:RuleList'],
		},
	];

	constructor(
		public dialog: MatDialog,
		private translator: TranslateService,
		private cuttingService: CuttingService,
		private notificationService: NotificationService,
		private cuttingRuleExcelService: CuttingRuleExcelService,
		private injector: Injector,
	) {}

	ngOnDestroy() {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	ngOnInit(): void {
		this.isLoading = true;
		this.loadData();
	}

	getSetups(account?: IAccount) {
		return this.cuttingService.getSetups(account).pipe(
			takeUntil(this.unsubscribe),
			tap((setups) => {
				this.setupNames = new Map(setups.items.map((setup) => [setup.self, setup.name]));
			}),
			catchError((error: HttpErrorResponse) => {
				this.handleError(error);
				return throwError(() => error);
			}),
		);
	}

	loadData(account?: IAccount): void {
		this.getSetups(account)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.loadRules();
			});
	}

	loadRules(): void {
		this.cuttingService
			.getRules()
			.pipe(
				takeUntil(this.unsubscribe),
				tap((data) => {
					this.isLoading = true;
					this.countSubject$.next(data.items.length);
				}),
				map((data) => {
					const rule$ = data.items.map((rule) => {
						return {
							self: rule.self,
							name: rule.name,
							disableOptimization: rule.disableOptimization,
							disableOptimizationLabel: rule.disableOptimization ? this.inactiveKey : this.activeKey,
							useCoatingInOptimization: rule.useCoatingInOptimization ?? false,
							useCoatingInOptimizationLabel: rule.useCoatingInOptimization ? this.activeKey : this.inactiveKey,
							priority: rule.priority ?? null,
							description: rule.description,
							batchType: rule.batchType?.self ? decodeURIComponent(this.cuttingService.getLastSegment(rule.batchType.self)) : '',
							material: decodeURIComponent(this.cuttingService.getLastSegment(rule.material.self)),
							profile: decodeURIComponent(this.cuttingService.getLastSegment(rule.profile?.self || '')),
							machine: decodeURIComponent(this.cuttingService.getLastSegment(rule.machine.self)),
							setup: rule.setup.self,
							setupName: this.setupNames.get(rule.setup.self),
							segregation: rule.segregation.identifier,
							optimization: rule.optimization.identifier,
							updatedDate: aciesDateFormat(new Date(rule.meta.updatedAt), true, false),
							updatedAtEpoch: rule.meta.updatedAt,
							updatedBy: this.cuttingService.getLastSegment(rule.meta.updatedBy.self),
							logisticsFormula: rule.logisticsFormula ?? null,
						};
					});
					return rule$;
				}),
				finalize(() => {
					this.isLoading = false;
				}),
			)
			.subscribe((rules$) => {
				this.tableSource.data = rules$;
			});
	}

	handleSelected(listOfSelected: object[]): void {
		this.selected = listOfSelected;
	}

	handleRowMenuAction(data: object): void {
		switch (data['action'].handler) {
			case 'editMasterdataRow':
				this.openCreateUpdateRule(data['data']);
				break;
			case 'deleteMasterdataRow':
				this.onDeleteRule(data['data']);
				break;
			default:
				break;
		}
	}

	handleReloadData(_: object): void {
		// Empty function to enable client side only pagination
		// this.loadData(data['pageSize'] * data['pageNumber']);
	}

	openCreateUpdateRule(setupData: object = null) {
		const dialogRef = this.dialog.open(CreateUpdateRuleDialogComponent, {
			disableClose: true,
			width: '650px',
			data: setupData,
		});
		dialogRef.componentInstance.saved.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
			this.loadData();
		});
	}

	private handleError(error: HttpErrorResponse) {
		if (error.status === 0) {
			// A client-side or network error occurred. Handle it accordingly.
			console.error('An error occurred:', error.error);
		} else {
			// The backend returned an unsuccessful response code.
			// The response body may contain clues as to what went wrong.
			console.error(`Backend returned code ${error.status}, body was: `, error.error);
		}
		// Return an observable with a user-facing error message.
		return throwError(() => new Error('Something bad happened; please try again later.'));
	}

	onDeleteRule(rule: IResponseRule) {
		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('masterdata.cutting.delete.rules.heading'),
				content: this.translator.instant('masterdata.cutting.delete.rules.content', {
					name: rule.name,
				}),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				if (result) {
					this.cuttingService
						.delete(rule.self)
						.pipe(catchError(this.handleError))
						.subscribe(() => {
							this.notificationService.notify(['masterdata.cutting.delete.rules.notification.header'], ['masterdata.cutting.delete.rules.notification.content', { name: rule.name }], 'success');
							this.loadData();
						});
				}
			});
	}

	exportRules(): void {
		this.cuttingService
			.getRules()
			.pipe(
				switchMap((rules) => this.cuttingRuleExcelService.exportData(rules.items)),
				takeUntil(this.unsubscribe),
			)
			.subscribe({
				next: () => {
					this.notificationService.notify(['masterdata.cutting.ruleImportExport.export.notification.success.header'], ['masterdata.cutting.ruleImportExport.export.notification.success.content'], 'success');
				},
				error: (error: Error) => {
					console.error('Failed to export rules', error);
					this.notificationService.notify(['masterdata.cutting.ruleImportExport.export.notification.error.header'], ['masterdata.cutting.ruleImportExport.export.notification.error.content'], 'error');
					this.dialog.open(TransferErrorPopupComponent, {
						width: '450px',
						data: { message: error.message },
					});
				},
			});
	}

	onImport(): void {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'masterdata.cutting.ruleImportExport.import.title',
				subHeadingKey: 'masterdata.cutting.ruleImportExport.import.subtitle',
				confirmButtonKey: 'masterdata.cutting.ruleImportExport.import.button',
				notificationHeader: 'masterdata.cutting.ruleImportExport.import.notification.success.header',
				notificationContent: 'masterdata.cutting.ruleImportExport.import.notification.success.content',
				notificationErrorHeader: 'masterdata.cutting.ruleImportExport.import.notification.error.header',
				notificationErrorContent: 'masterdata.cutting.ruleImportExport.import.notification.error.content',
				notificationClientErrorHeader: 'masterdata.cutting.ruleImportExport.import.notification.clientError.header',
				notificationClientErrorContent: 'masterdata.cutting.ruleImportExport.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.injector.get(ImportValidatorService),
				data: this.cuttingRuleExcelService,
			},
		});

		dialogRef.afterClosed().subscribe(() => {
			this.loadData();
		});
	}

	downloadTemplate(): void {
		this.cuttingRuleExcelService.getTemplate();
		this.notificationService.notify(['masterdata.cutting.ruleImportExport.import.template.notification.header'], ['masterdata.cutting.ruleImportExport.import.template.notification.content'], 'success');
	}
}
